import type { RegisteredComponent } from "@builder.io/sdk-react";
import { isEditing, isPreviewing } from "@builder.io/sdk-react";

import { PartnerForm } from "~/hec/components/organisms/partner-form/partner-form";
import type {
  PartnerFormCopy,
  PartnerFormEditorState,
  PartnerFormTheme,
} from "~/hec/components/organisms/partner-form/types";

import { partnerFormInputs } from "./inputs";

/**
 * COM-737 — the Become a Partner form as a drop-in block. Thin wrapper: maps
 * Builder inputs onto the `PartnerForm` organism and spreads `attributes` on
 * the root (`noWrap` contract, CLAUDE.md §4). Page-body block only; the navy
 * card, heading and intro copy are authored content around it.
 *
 * `name: "PartnerForm"` is the contract published content references; it
 * cannot be renamed once content uses it.
 */
interface BuilderPartnerFormProps extends PartnerFormCopy {
  theme?: PartnerFormTheme;
  editorState?: PartnerFormEditorState;
  attributes?: Record<string, unknown> & { className?: string };
}

const BuilderPartnerForm = ({
  theme,
  submitLabel,
  successMessage,
  failureMessage,
  editorState = "ready",
  attributes,
}: BuilderPartnerFormProps) => {
  const { className, ...rest } = attributes ?? {};
  const inEditor = isEditing() || isPreviewing();
  return (
    <PartnerForm
      theme={theme === "light" ? "light" : "dark"}
      copy={{ submitLabel, successMessage, failureMessage }}
      editorState={inEditor ? editorState : undefined}
      className={className}
      {...rest}
    />
  );
};

export const PartnerFormDefinition = {
  component: BuilderPartnerForm,
  name: "PartnerForm",
  friendlyName: "Become a Partner form (FSA + HSA)",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/handshake.svg",
  inputs: partnerFormInputs,
} satisfies RegisteredComponent;

export default BuilderPartnerForm;
